import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import HCaptcha from '@hcaptcha/react-hcaptcha';

import { toast } from 'react-toastify';

import { useLocation, useNavigate } from 'react-router-dom';

import { Input, Checkbox, BackButton, Button, TextLink } from '../shared/ui';
import ConfirmAuth from '../components/confirm-auth';

import { useAppSelector } from '../hooks/redux';
import { selectUser } from '../store/auth.slice';
import { useRegistrationMutation } from '../services/auth.api';
import { LANDING } from '../configs/api/api.config';

const schema = yup.object({
  email: yup.string().email('Неверный формат Email').required('Введите Email'),
  referalCode: yup.string().optional(),
  haveEducation: yup.boolean().oneOf([true], 'Подтвердите наличие образования').required(),
  agreePrivacy: yup.boolean().oneOf([true], 'Примите условия соглашения').required(),
});

type FormData = yup.InferType<typeof schema>;

const Registration = () => {
  const [captcha, setCaptcha] = useState('');
  const [resetCaptcha, setResetCaptcha] = useState(0);
  const [isInputAuthCode, setIsInputAuthCode] = useState(false);

  const location = useLocation();

  const { handleSubmit, setError, watch, control, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      haveEducation: false,
      agreePrivacy: false,
      referalCode: '',
    },
    mode: 'onBlur',
  });
  const email = watch('email');
  const [registration] = useRegistrationMutation();
  const userData = useAppSelector(selectUser);
  const navigate = useNavigate();

  useEffect(() => {
    // Get promoCode from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const promoCode = searchParams.get('promoCode');

    // Set the referalCode field's initial value if promoCode exists
    if (promoCode) {
      reset({
        email: '',
        haveEducation: false,
        agreePrivacy: false,
        referalCode: promoCode,
      });
    }

    if (userData) {
      navigate('/consultations', {
        state: { previousLocationPathname: location.pathname },
      });
    }
  }, [userData, navigate, location.pathname, location.search, reset]);

  const onSubmit = async (data: FormData) => {
    if (!captcha) {
      toast.error('Пройдите капчу');

      return;
    }

    try {
      const res = await registration({
        captcha,
        email: data.email,
        referalCode: data.referalCode,
        agree_to_receive_emails: false,
        have_education: data.haveEducation,
      }).unwrap();

      if (res) {
        toast.success('Вы успешно зарегистрировались');
        setIsInputAuthCode(true);
      }
    } catch (e: any) {
      if (e.status === 422 && e.data?.detail) {
        setError('email', { message: e.data.detail });
      } else {
        toast.error('Произошла ошибка');
      }
    } finally {
      setResetCaptcha((prev) => prev + 1);
    }
  };

  return (
    <div className="flex min-h-[calc(100dvh-50px)] w-screen items-center justify-center px-[15px] lg:min-h-[calc(100dvh-80px)]">
      {!isInputAuthCode ? (
        <div className="w-[420px] sm:w-[550px] max-h-[90%] overflow-y-auto rounded-popup bg-white p-[10px] md:p-[50px] short:p-[10px]">
          <div className="flex items-center text-center text-display-s md:text-display-l short:text-display-s mb-2.5">
            <BackButton className="-m-1.5" to="/" />
            <span className="grow text-center">Регистрация</span>
          </div>

          <div className="text-center sm:px-[30px] short:px-[0px] text-ui md:text-body-l text-ink-muted mb-5 short:mb-3 md:mb-10">
            Зарегистрируйтесь, чтобы получить доступ к личному кабинету
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-[12px] mb-4">
            <div>
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState: { error } }) => (
                  <Input
                    size="lg"
                    label="Email"
                    placeholder="Введите Email"
                    error={error?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </div>
            <div>
              <Controller
                control={control}
                name="referalCode"
                render={({ field }) => (
                  <Input
                    size="lg"
                    label="Пригласительный ключ (если есть)"
                    placeholder="Введите ключ"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
                defaultValue=""
              />
            </div>

            <div className="flex flex-col gap-[16px] mt-4 mb-4">
              <Controller
                control={control}
                name="haveEducation"
                render={({ field, fieldState: { error } }) => (
                  <Checkbox
                    size="sm"
                    label="Я подтверждаю, что у меня есть высшее психологическое образование"
                    checked={field.value}
                    onChange={field.onChange}
                    invalid={!!error?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="agreePrivacy"
                render={({ field, fieldState: { error } }) => (
                  <Checkbox
                    size="sm"
                    label={
                      <>
                        Я принимаю условия{' '}
                        <TextLink
                          tone="inherit"
                          to={`${LANDING}/wp-content/uploads/2025/03/polzovatelskoe_soglashenie_logosterapi_rev.pdf`}
                          target="_blank"
                        >
                          Пользовательского соглашения
                        </TextLink>{' '}
                        и даю{' '}
                        <TextLink
                          tone="inherit"
                          to={`${LANDING}/wp-content/uploads/2025/03/politika_konfidenczialnosti_logosterapi_rev.pdf`}
                          target="_blank"
                        >
                          согласие на обработку персональных данных
                        </TextLink>
                      </>
                    }
                    checked={field.value}
                    onChange={field.onChange}
                    invalid={!!error?.message}
                  />
                )}
              />
            </div>

            <div className="mb-[30px]">
              <HCaptcha
                sitekey="92e461fa-ac46-44ce-8a04-d85a6b185f9e"
                onVerify={(token) => setCaptcha(token)}
                key={resetCaptcha}
                onExpire={() => {
                  setCaptcha('');
                  setResetCaptcha((prev) => prev + 1);
                }}
              />
            </div>

            <Button size="lg" fullWidth type="submit">
              Зарегистрироваться
            </Button>
          </form>

          <div className="text-center">
            <div className="text-ui md:text-body-l text-ink-muted">Есть аккаунт?</div>
            <TextLink href="/" className="text-ui md:text-body-l">
              Войти
            </TextLink>
          </div>
        </div>
      ) : (
        <ConfirmAuth onBackToAuth={() => setIsInputAuthCode(false)} email={email} confirmCodex={true} />
      )}
    </div>
  );
};

export default Registration;
